import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";

import { cn } from "@/features/knowledge-graph/utils/styles";

/** Shared chrome for every status banner — depends on no prop (SE-8019). */
const containerClasses = "px-4 py-2";
const textClasses = "text-content-on-accent text-center";

/**
 *
 */
interface ConnectionStatusViewProps {
  isPlaceholder: boolean;
  displayState: string | null;
  reconnectAttempts: number;
}

/**
 * Banner per connection state, keyed by `displayState`
 * (cognitive-complexity-lookup-tables). This was a five-branch `switch` inside
 * the component; folding it into nested ternaries to satisfy the
 * expression-body rule would have traded one lint failure for a complexity
 * one, which is the trap LeagueGprSpectrum and UpdateFeedList are still parked
 * on. A table keyed by state leaves the View with a single branch.
 *
 * `connected` and `disconnected` render nothing on purpose: the connection is
 * established on demand, so a "disconnected" banner on first paint would be
 * noise rather than information.
 */
const BANNER_BY_STATE: Record<
  string,
  (reconnectAttempts: number) => React.ReactElement | null
> = {
  connecting: () => (
    <Box className={cn(containerClasses, "bg-primary-material")}>
      <Text variant="title-sm-medium" className={textClasses}>
        Connecting...
      </Text>
    </Box>
  ),
  connected: () => null,
  reconnecting: (reconnectAttempts: number) => (
    <Box className={cn(containerClasses, "bg-status-reconnecting")}>
      <Text variant="title-sm-medium" className={textClasses}>
        Reconnecting... (Attempt {reconnectAttempts})
      </Text>
    </Box>
  ),
  error: () => (
    <Box className={cn(containerClasses, "bg-status-error-material")}>
      {/*
        SE-7078: this used to promise "Will retry automatically", which was
        never true — nothing in the chat flow re-sends a failed query, and
        auto-reconnect is explicitly disabled in `useSSE`. In the hang that
        ticket fixes, that line appeared next to the error banner and told the
        user to wait for a retry that was never coming. The copy now names the
        affordance that actually exists.
      */}
      <Text variant="title-sm-medium" className={textClasses}>
        Connection error. Send your question again to retry.
      </Text>
    </Box>
  ),
  disconnected: () => null,
};

/** Shown when the SSE endpoint is unconfigured — a setup fault, not a state. */
const PLACEHOLDER_BANNER = (
  <Box className="gap-1 bg-status-warning px-4 py-2">
    <Text
      variant="title-sm-medium"
      className="text-center text-content-primary"
    >
      ⚠️ SSE endpoint not configured
    </Text>
  </Box>
);

const ConnectionStatusView = React.memo<ConnectionStatusViewProps>(
  ({ isPlaceholder, displayState, reconnectAttempts }) =>
    isPlaceholder
      ? PLACEHOLDER_BANNER
      : (BANNER_BY_STATE[displayState ?? ""]?.(reconnectAttempts) ?? null)
);

ConnectionStatusView.displayName = "ConnectionStatusView";
export default memo(ConnectionStatusView);
